/* DCG v2-10 · 10 OCT 2026 · D-SP-31 · the phone product page: the stock line by the price, and the buy bar */
/* =========================================================
   Desert City Games — the phone product page (D-SP-31)
   File: assets/dcg-pdp-phone.js   (styles: assets/dcg-pdp-phone.css)
   Loaded by sections/product-template.liquid.

   BELOW 768px ONLY. At 768px and up this file draws nothing: the two things
   it adds are in the page but not shown, and the page is as it was.

   1. THE STOCK LINE
      The page's own stock line ("In stock" / "Sold out") is under Add to
      Cart, a screen and a half down on a phone. A copy of what it says is
      put beside the price, where it is on the first screen.

   2. THE BUY BAR
      A bar fixed to the bottom of the screen while the page's own Add to
      Cart button is off the screen, above it or below it: the product's
      short name, the chosen condition, the price, and one button. When the
      page's own button is on the screen the bar is not.

      IT IS NOT A SECOND FORM. The bar's button presses the page's own
      button (a click on that element) and does nothing else, so the cart
      enforcer, the quantity limits, the zero-price rule and every other
      check run exactly as they do from the page. The bar never adds to the
      cart by itself, and there is no code here that could.

      It shows the state the page is in, read from the page each time:
        buyable          the page's button's words; a press goes to it
        sold out         "Sold Out"; the button is off, as the page's is
        coming soon      "Coming Soon"; the same
        price pending    "Price pending"; the same
        in-store only    "In-store only", and the page's own "Contact us" link
        notify me        where the page has its "Notify me" form (sold out,
                         coming soon, an in-store pre-order) the bar's button
                         is "Notify me": it goes to that form and puts the
                         cursor in its email field
      Where the page's button cannot add to the cart, neither can the bar's.
      It follows a change of condition or of quantity on the page.

      It is hidden while the phone menu, a dialog or a text field is in use.
      The return-to-top button moves up by the bar's height while the bar
      is shown (html.dcg-buybar-on, --dcg-buybar-h; assets/dcg-pdp-phone.css).

   Everything read here is the page's own markup; nothing is asked of the
   network. No jQuery.
   ========================================================= */
(function () {
  'use strict';

  window.DCG = window.DCG || {};
  if (window.DCG.pdpPhone) return;

  const root = document.documentElement;
  const mq = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
  const isPhone = () => !!mq && mq.matches;

  const ON = 'dcg-buybar-on';                   // on <html> while the bar is shown
  const BAR_ID = 'dcg-buybar';
  const STOCK_CLASS = 'dcg-pdp-stock';

  const clean = s => String(s || '').replace(/\s+/g, ' ').trim();
  const shown = el => !!el && el.getClientRects().length > 0 && window.getComputedStyle(el).visibility !== 'hidden';
  // an element's own words, without what is hidden inside it and without icon-font letters
  function words(el) {
    if (!el) return '';
    const copy = el.cloneNode(true);
    copy.querySelectorAll('.dcg-hide, .hide, .visually-hidden, i, svg, [hidden], [aria-hidden="true"]').forEach(n => n.remove());
    return clean(copy.textContent);
  }

  // ── The page's own pieces ──
  const info = () => document.querySelector('.dcg-product-info-col');
  const section = () => document.querySelector('[id^="ProductSection-"]');
  const pageButton = () => document.querySelector('.dcg-product-purchase button.addToCart');
  const buyRow = () => document.querySelector('.dcg-product-purchase [data-dcg-buy-row]');
  const retailNotice = () => document.querySelector('.dcg-product-purchase .dcg-retail-notice--pdp');
  const notifyField = () => document.querySelector('#dcg-notify input[type="email"]');
  const priceWrap = () => document.querySelector('.dcg-product-meta .dcg-product-price-wrap');
  const menuOpen = () => !!document.body && document.body.classList.contains('dcg-mobile-menu-open');
  const dialogOpen = () => !!document.body && document.body.classList.contains('modal-open');
  function typing() {
    const a = document.activeElement;
    if (!a || !a.tagName) return false;
    if (a.tagName === 'TEXTAREA') return true;
    return a.tagName === 'INPUT' && !/^(button|submit|checkbox|radio|range|file|hidden|image|reset)$/i.test(a.type || 'text');
  }

  // "Radical Beam!! [Romance Dawn]" is "Radical Beam!!" in the bar: the set, in its square
  // brackets at the end of a single's name, is left off. A name with no brackets is whole.
  function shortName(full) {
    let s = clean(full);
    for (let i = 0; i < 3; i++) { const cut = s.replace(/\s*\[[^\]]*\]\s*$/, ''); if (!cut || cut === s) break; s = cut; }
    return s;
  }

  // how many of the chosen variant the shop has, as the page's own select holds it (data-stock)
  function onHand(col) {
    const master = col.querySelector('select[id^="ProductSelect-"]');
    const chosen = col.querySelector('input[name="prduct-variant"]');
    const id = chosen && chosen.value ? chosen.value : (master ? master.value : '');
    const opt = master ? [...master.options].find(o => o.value === String(id)) : null;
    const n = opt ? parseInt(opt.getAttribute('data-stock'), 10) : NaN;
    return isFinite(n) ? n : 1;
  }

  // ── What the page says now ──
  function read() {
    const col = info();
    if (!col) return null;
    const sec = section();
    const titleEl = col.querySelector('h1.product-single__title') || document.querySelector('.product-single__title');
    const full = clean(titleEl ? titleEl.textContent : document.title);
    const btn = pageButton();
    const btnShown = shown(btn);
    const notice = retailNotice();
    const row = buyRow();
    const field = notifyField();

    // The chosen condition: what the page's own select has chosen (its option reads "Near Mint —
    // $0.40"; the part before the dash is the variant's name). Not the label beside the price:
    // the page writes that label on load only, and it stays on the first condition after another
    // is picked (seen in Chrome, 10 OCT 2026). A product with one variant has no select and no
    // condition; then the label is read, and it is hidden for such a product.
    const pickEl = col.querySelector('[data-dcg-variant-select]');
    const picked = pickEl && pickEl.selectedIndex >= 0 ? pickEl.options[pickEl.selectedIndex] : null;
    const condEl = col.querySelector('.dcg-price-condition-label');
    const cond = picked ? clean(picked.textContent.split('—')[0])
      : condEl && !condEl.classList.contains('dcg-hide') ? clean(condEl.textContent) : '';
    const priceEl = col.querySelector('[id^="ProductPrice-"]');
    const price = clean(priceEl ? priceEl.textContent : '');
    const qtyEl = col.querySelector('.dcg-product-purchase input[name="quantity"]');
    const qty = qtyEl && shown(qtyEl) ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;

    const btnText = btnShown ? words(btn) : '';
    const btnOff = !btnShown || btn.disabled || btn.getAttribute('aria-disabled') === 'true' ||
      (btn.classList.contains('nm-addToCart') && !btn.classList.contains('enable'));

    let kind, label = '';
    if (shown(notice)) {
      kind = 'instore';
      label = words(notice.querySelector('.dcg-retail-notice__text')) || 'In-store only';
    } else if (!btnShown) {
      kind = 'none';                                    // the page offers no button: an in-store pre-order
      label = words(col.querySelector('[data-dcg-preorder-info] .dcg-retail-pdp-info__sold-out'));
    } else if (!btnOff) {
      kind = 'buy';
    } else if (sec && sec.getAttribute('data-dcg-price-pending') === '1') {
      kind = 'pending'; label = btnText;
    } else if (row && row.classList.contains('dcg-cs-disabled')) {
      kind = 'soon'; label = btnText;
    } else {
      kind = 'soldout'; label = btnText;
    }

    // what the bar's button does
    let action = 'off', actionText = btnText;
    if (kind === 'buy') action = 'add';
    else if (kind === 'instore') {
      const link = notice.querySelector('a[href]');
      if (link) { action = 'link'; actionText = clean(link.textContent.replace(/[←-⇿]/g, '')); }
    }
    if (action === 'off' && shown(field)) {
      action = 'notify';
      const submit = document.querySelector('#dcg-notify input[type="submit"]');
      actionText = clean(submit && submit.value) || 'Notify me';
    }
    if (action === 'off' && !actionText) actionText = label;

    // the stock line
    let stock = '', stockKind = '';
    if (kind === 'instore') {
      const box = col.querySelector('[data-dcg-retail-info]');
      const have = box ? box.querySelector('.dcg-retail-pdp-info__qty') : null;
      stock = have ? words(have) : words(box ? box.querySelector('.dcg-retail-pdp-info__sold-out') : null);
      stockKind = have ? 'in' : 'out';
    } else if (kind === 'none') {
      stock = label; stockKind = 'out';
    } else if (kind === 'buy') {
      // The theme's own stock line (#inventory) is in the page and not shown, and its low-stock
      // line shows only at or under the number set in the theme. What is said here is one of the
      // page's own lines: the low-stock line when the page shows it; the button's words for a
      // pre-order or a back order; else the theme's own word for in stock.
      const low = col.querySelector('.progress-content');
      const badge = col.querySelector('.dcg-preorder-badge.dcg-pdp-badge');
      if (shown(low)) { stock = words(low.querySelector('.quantity-value') || low); stockKind = 'low'; }
      else if (badge || onHand(col) <= 0) { stock = badge ? words(badge) : btnText; stockKind = 'wait'; }
      else { stock = words(col.querySelector('#inventory .instock')); stockKind = 'in'; }
    } else {
      stock = label; stockKind = kind === 'soldout' ? 'out' : 'wait';
    }

    return {
      kind, label, action, actionText, full, name: shortName(full), cond, price, qty, stock, stockKind,
      href: action === 'link' ? notice.querySelector('a[href]').getAttribute('href') : ''
    };
  }

  // ── 1. The stock line ──
  function paintStock(s) {
    const wrap = priceWrap();
    if (!wrap || !wrap.parentNode) return;
    let line = wrap.parentNode.querySelector('.' + STOCK_CLASS);
    if (!s.stock) { if (line && !line.hidden) line.hidden = true; return; }
    if (!line) {
      line = document.createElement('p');
      line.className = STOCK_CLASS;
      wrap.parentNode.insertBefore(line, wrap.nextSibling);
    }
    if (line.hidden) line.hidden = false;
    if (line.textContent !== s.stock) line.textContent = s.stock;
    if (line.getAttribute('data-dcg-stock') !== s.stockKind) line.setAttribute('data-dcg-stock', s.stockKind);
  }

  // ── 2. The bar ──
  let bar = null, nameEl, condEl, qtyEl, stateEl, priceEl, button, link;
  const make = (tag, cls) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };
  function build() {
    if (bar && bar.isConnected) return bar;
    bar = make('div', 'dcg-buybar');
    bar.id = BAR_ID;
    bar.setAttribute('role', 'region');
    bar.setAttribute('data-dcg-buybar', 'hide');
    const text = make('div', 'dcg-buybar__text');
    nameEl = make('span', 'dcg-buybar__name');
    const meta = make('span', 'dcg-buybar__meta');
    condEl = make('span', 'dcg-buybar__cond');
    qtyEl = make('span', 'dcg-buybar__qty');
    stateEl = make('span', 'dcg-buybar__state');
    priceEl = make('span', 'dcg-buybar__price');
    meta.appendChild(condEl); meta.appendChild(qtyEl); meta.appendChild(stateEl); meta.appendChild(priceEl);
    text.appendChild(nameEl); text.appendChild(meta);
    button = make('button', 'dcg-buybar__btn');
    button.type = 'button';
    link = make('a', 'dcg-buybar__btn dcg-buybar__btn--link');
    bar.appendChild(text); bar.appendChild(button); bar.appendChild(link);
    button.addEventListener('click', press);
    document.body.appendChild(bar);
    return bar;
  }
  const put = (el, text) => { if (el.textContent !== text) el.textContent = text; if (el.hidden !== !text) el.hidden = !text; };
  const attr = (el, name, value) => { if (value === null) { if (el.hasAttribute(name)) el.removeAttribute(name); } else if (el.getAttribute(name) !== value) el.setAttribute(name, value); };

  let state = null;
  function paintBar(s) {
    build();
    attr(bar, 'aria-label', 'Buy: ' + s.full);
    attr(bar, 'data-dcg-kind', s.kind);
    put(nameEl, s.name);
    put(condEl, s.cond);
    put(qtyEl, s.qty > 1 ? 'Qty ' + s.qty : '');
    put(stateEl, s.kind === 'buy' ? '' : s.label);
    put(priceEl, s.price);
    const about = [s.name, s.cond, s.qty > 1 ? 'quantity ' + s.qty : '', s.price].filter(Boolean).join(', ');
    const isLink = s.action === 'link';
    if (button.hidden !== isLink) button.hidden = isLink;
    if (link.hidden !== !isLink) link.hidden = !isLink;
    if (isLink) {
      put(link, s.actionText);
      attr(link, 'href', s.href);
      attr(link, 'aria-label', s.actionText + ': ' + s.label + ', ' + about);
    } else {
      put(button, s.actionText);
      const off = s.action === 'off';
      if (button.disabled !== off) button.disabled = off;
      attr(button, 'data-dcg-buybar-action', s.action);
      attr(button, 'aria-label', s.actionText + ': ' + (s.action === 'notify' && s.label ? s.label + ', ' : '') + about);
    }
  }

  // A press. "add": a click on the page's own button, and nothing more. "notify": to the page's
  // own form. There is no third thing it can do.
  function press(e) {
    if (e) e.preventDefault();
    const s = state;
    if (!s) return;
    if (s.action === 'add') {
      const b = pageButton();
      if (b && !b.disabled) b.click();
    } else if (s.action === 'notify') {
      const f = notifyField();
      if (!f) return;
      let jump = false;
      try { jump = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) {}
      try { f.scrollIntoView({ block: 'center', behavior: jump ? 'instant' : 'smooth' }); } catch (_) { f.scrollIntoView(); }
      try { f.focus({ preventScroll: true }); } catch (_) { try { f.focus(); } catch (__) {} }
    }
  }

  // ── Shown, or not ──
  // What the bar stands in for: the page's own button; where there is none, what the page has in
  // its place.
  function anchor() {
    const b = pageButton();
    if (shown(b)) return b;
    const n = retailNotice();
    if (shown(n)) return n;
    const f = notifyField();
    if (shown(f)) return f;
    return null;
  }
  let isOn = false;
  function onScreen(el) {
    const r = el.getBoundingClientRect();
    const head = document.querySelector('.dcg-mobile-bar');
    let top = 0;
    if (head && window.getComputedStyle(head).position === 'fixed') top = Math.max(0, head.getBoundingClientRect().bottom);
    // while the bar is up it covers the foot of the screen: the page's button is not "on screen" behind it
    const foot = (window.innerHeight || 0) - (isOn && bar ? bar.offsetHeight : 0);
    return r.bottom > top && r.top < foot;
  }
  function place() {
    if (!bar) return;
    const a = isPhone() && state ? anchor() : null;
    const on = !!a && !onScreen(a) && !menuOpen() && !dialogOpen() && !typing();
    if (on !== isOn) {
      isOn = on;
      bar.setAttribute('data-dcg-buybar', on ? 'show' : 'hide');
      root.classList.toggle(ON, on);
    }
    if (on) {
      const h = bar.offsetHeight + 'px';
      if (root.style.getPropertyValue('--dcg-buybar-h') !== h) root.style.setProperty('--dcg-buybar-h', h);
    }
  }

  // ── Keeping up with the page ──
  let queued = false;
  function refresh() {
    queued = false;
    let s = null;
    try { s = read(); } catch (_) { s = null; }
    state = s;
    if (!s) return;
    try { paintStock(s); } catch (_) {}
    try { paintBar(s); } catch (_) {}
    try { place(); } catch (_) {}
  }
  function soon() { if (queued) return; queued = true; window.requestAnimationFrame(refresh); }
  let ticking = false;
  function onScroll() { if (ticking) return; ticking = true; window.requestAnimationFrame(() => { ticking = false; try { place(); } catch (_) {} }); }

  function start() {
    const col = info();
    if (!col) return;
    refresh();
    // the theme, the price-pending rule, the quantity guard and the enforcer all write to the
    // page's button, price and labels; whatever they leave is read again
    try {
      new MutationObserver(soon).observe(col, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'hidden', 'style', 'value'] });
      const sec = section();
      if (sec) new MutationObserver(soon).observe(sec, { attributes: true, attributeFilter: ['data-dcg-price-pending'] });
      if (document.body) new MutationObserver(soon).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    } catch (_) {}
    // a quantity typed or stepped changes an input's value, which no observer sees
    ['change', 'input', 'click'].forEach(type => col.addEventListener(type, () => { soon(); window.setTimeout(soon, 250); }, true));
    document.addEventListener('focusin', soon);
    document.addEventListener('focusout', () => window.setTimeout(soon, 60));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', soon);
    if (mq) { try { mq.addEventListener('change', soon); } catch (_) { try { mq.addListener(soon); } catch (__) {} } }
    window.addEventListener('load', soon);
    window.setTimeout(soon, 600);
    window.setTimeout(soon, 2000);
  }

  window.DCG.pdpPhone = { refresh, state: () => state, shown: () => isOn };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { try { start(); } catch (_) {} });
  else { try { start(); } catch (_) {} }
})();
